import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLog, scaleSequential } from "d3-scale";
import { interpolateOranges } from "d3-scale-chromatic";
import { Loader2, ArrowLeft, Globe2, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { friendlyError } from "@/lib/friendlyError";

// Public TopoJSON world atlas — same one react-simple-maps docs use.
// `countries-50m` has full coverage of every UN-recognised nation
// (including small East African states that the 110m topojson omits —
// Ethiopia, Somalia, Eritrea, Djibouti, Burundi, Rwanda, Comoros, etc.).
// Trade-off is ~460 KB vs 100 KB — a one-time cold load for admins only.
const WORLD_TOPO = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

// ISO numeric → ISO alpha-2 lookup subset (populated on demand from the topo).
// We match on `geo.properties.name` + `geo.id` fallback.

/**
 * Revenue Heat Map with 3-level drill: world → country → region → city.
 * MRR density (USD) drives the choropleth fill.
 */
export default function RevenueHeatMap() {
  const [level, setLevel] = useState("country");
  const [selCountry, setSelCountry] = useState(null);  // e.g. { code, label }
  const [selRegion, setSelRegion] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overlay, setOverlay] = useState("revenue");   // 'revenue' | 'churn'
  const [churnOverlay, setChurnOverlay] = useState([]);  // [{country, top_reason, top_reason_label, churn_count}]

  const params = useMemo(() => {
    const p = { level, hours: 24 * 30 };
    if (selCountry?.code) p.country = selCountry.code;
    if (selRegion?.key) p.region = selRegion.key;
    return p;
  }, [level, selCountry, selRegion]);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/pulse/revenue-map`, { params })
      .then((r) => setPayload(r.data))
      .catch((err) => toast.error(friendlyError(err, "Failed to load revenue map")))
      .finally(() => setLoading(false));
  }, [params]);

  // Churn-overlay fetch — one-shot, country level only.
  useEffect(() => {
    if (level !== "country") return;
    axios
      .get(`${API}/admin/pulse/churn-by-country`)
      .then((r) => setChurnOverlay(r.data || []))
      .catch(() => { /* non-blocking */ });
  }, [level]);

  // Build lookup from country_code → row (full metrics) for map fill + tooltip.
  const rowByCountry = useMemo(() => {
    if (!payload || payload.level !== "country") return {};
    const m = {};
    payload.rows.forEach((r) => { m[r.key] = r; });
    return m;
  }, [payload]);

  const mrrByCountry = useMemo(() => {
    const m = {};
    Object.entries(rowByCountry).forEach(([k, r]) => { m[k] = r.mrr_usd; });
    return m;
  }, [rowByCountry]);

  // Churn lookup: country_code → { top_reason, label, count }
  const churnByCountry = useMemo(() => {
    const m = {};
    churnOverlay.forEach((c) => { m[c.country] = c; });
    return m;
  }, [churnOverlay]);

  // Floating tooltip anchored to the cursor.
  // ``row`` carries { label, mrr_usd, active_users, arpu_usd } for the hovered geo.
  const [tooltip, setTooltip] = useState(null);
  const handleGeoEnter = (row, evt) => {
    if (!row) return;
    setTooltip({ row, x: evt.clientX, y: evt.clientY });
  };
  const handleGeoMove = (evt) => {
    setTooltip((prev) => (prev ? { ...prev, x: evt.clientX, y: evt.clientY } : prev));
  };
  const handleGeoLeave = () => setTooltip(null);

  const colorScale = useMemo(() => {
    const values = Object.values(mrrByCountry).filter((v) => v > 0);
    if (values.length === 0) return () => "#1e293b";
    const max = Math.max(...values);
    const min = Math.min(...values);
    const s = scaleLog().domain([Math.max(min, 0.01), max]).range([0.2, 1]);
    return (v) => (v > 0 ? interpolateOranges(s(v)) : "#1e293b");
  }, [mrrByCountry]);

  // Churn-overlay palette: dominant reason colour per country.
  const CHURN_COLOR = {
    too_expensive:    "#f59e0b",   // amber
    missing_feature:  "#22d3ee",   // cyan
    too_complicated:  "#f43f5e",   // rose
    other:            "#64748b",   // slate
  };
  const churnFill = (alpha2) => {
    const c = churnByCountry[alpha2];
    if (!c) return "#1e293b";
    return CHURN_COLOR[c.top_reason] || CHURN_COLOR.other;
  };

  const drillDown = (row) => {
    if (level === "country") {
      setSelCountry({ code: row.key, label: row.label });
      setLevel("region");
    } else if (level === "region") {
      setSelRegion({ key: row.key, label: row.label });
      setLevel("city");
    }
  };

  const drillUp = () => {
    if (level === "city") {
      setSelRegion(null);
      setLevel("region");
    } else if (level === "region") {
      setSelCountry(null);
      setLevel("country");
    }
  };

  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-4"
      data-testid="revenue-heat-map"
    >
      {/* Header + breadcrumb */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-amber-400" />
            Revenue Heat Map
          </h3>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid="revenue-map-breadcrumb">
            <span>World</span>
            {selCountry && (<><span className="text-slate-600">›</span><span className="text-amber-300">{selCountry.label}</span></>)}
            {selRegion && (<><span className="text-slate-600">›</span><span className="text-amber-300">{selRegion.label}</span></>)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {level === "country" && (
            <div className="inline-flex rounded-lg border border-slate-700 bg-slate-900 overflow-hidden text-xs" data-testid="revenue-map-overlay-toggle">
              <button
                type="button"
                onClick={() => setOverlay("revenue")}
                className={`px-3 py-1.5 ${overlay === "revenue" ? "bg-amber-500 text-slate-950 font-semibold" : "text-slate-400 hover:text-white"}`}
                data-testid="revenue-map-overlay-revenue"
              >
                Revenue
              </button>
              <button
                type="button"
                onClick={() => setOverlay("churn")}
                className={`px-3 py-1.5 ${overlay === "churn" ? "bg-rose-500 text-slate-950 font-semibold" : "text-slate-400 hover:text-white"}`}
                data-testid="revenue-map-overlay-churn"
              >
                Churn
              </button>
            </div>
          )}
          {(selCountry || selRegion) && (
            <Button variant="outline" size="sm" onClick={drillUp} data-testid="revenue-map-drill-up">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          {payload && (
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">MRR at this level</div>
              <div className="text-xl font-bold text-amber-400" data-testid="revenue-map-total-mrr">
                ${payload.total_mrr_usd?.toLocaleString?.() ?? "0"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* World SVG (only at country level) */}
      {level === "country" && (
        <div className="relative bg-slate-950/50 rounded-xl overflow-hidden border border-slate-800">
          <ComposableMap
            projectionConfig={{ scale: 140 }}
            style={{ width: "100%", height: "auto" }}
            data-testid="revenue-map-svg"
          >
            <Geographies geography={WORLD_TOPO}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  // world-atlas uses ISO 3166-1 alpha-3 in properties.name (long name).
                  // Our backend keys by alpha-2 (from CF-IPCountry).  We fall back to
                  // string-matching alpha-3 via a small runtime map when possible.
                  const alpha2 = ISO3_TO_ISO2[geo.id] || null;
                  const row = alpha2 ? rowByCountry[alpha2] : null;
                  const mrr = row?.mrr_usd || 0;
                  const churnMeta = alpha2 ? churnByCountry[alpha2] : null;
                  const fillColor = overlay === "churn" ? churnFill(alpha2) : colorScale(mrr);
                  const hoverRow = row || (alpha2
                    ? { label: geo.properties.name, mrr_usd: 0, active_users: 0, arpu_usd: 0 }
                    : null);
                  const hoverPayload = hoverRow
                    ? { ...hoverRow, _churn: churnMeta }
                    : null;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="#0f172a"
                      strokeWidth={0.4}
                      style={{
                        default: { outline: "none" },
                        hover: { fill: "#f59e0b", outline: "none", cursor: mrr > 0 ? "pointer" : "default" },
                        pressed: { outline: "none" },
                      }}
                      onMouseEnter={(evt) => handleGeoEnter(hoverPayload, evt)}
                      onMouseMove={handleGeoMove}
                      onMouseLeave={handleGeoLeave}
                      onClick={() => {
                        if (mrr > 0 && alpha2) drillDown({ key: alpha2, label: geo.properties.name });
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          <div className="absolute bottom-2 left-3 right-3 flex justify-between items-end pointer-events-none text-[10px]">
            <div className="bg-slate-900/80 rounded px-2 py-1 text-slate-400">
              Click a highlighted country to drill down to state-level revenue
            </div>
            <HeatLegend />
          </div>

          {/* Floating cursor tooltip */}
          {tooltip && (
            <RevenueTooltip x={tooltip.x} y={tooltip.y} row={tooltip.row} />
          )}
        </div>
      )}

      {/* Data table (all levels) */}
      {loading ? (
        <div className="py-8 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading revenue…
        </div>
      ) : !payload || payload.rows.length === 0 ? (
        <div className="py-8 text-center text-slate-500 italic">
          No paid transactions with geo data at this level yet.
        </div>
      ) : (
        <div className="border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="revenue-map-table">
            <thead className="bg-slate-950 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-medium">
                  {level === "country" ? "Country" : level === "region" ? "State / Region" : "City"}
                </th>
                <th className="text-right px-3 py-2 font-medium">Total Revenue</th>
                <th className="text-right px-3 py-2 font-medium">Active Users</th>
                <th className="text-right px-3 py-2 font-medium">ARPU</th>
                <th className="text-right px-3 py-2 font-medium">Txns</th>
                <th className="text-right px-3 py-2 font-medium">Latest</th>
              </tr>
            </thead>
            <tbody>
              {payload.rows.map((r) => {
                const canDrill = level !== "city";
                const Icon = level === "country" ? Globe2 : level === "region" ? MapPin : Building2;
                return (
                  <tr
                    key={r.key}
                    onClick={() => canDrill && drillDown(r)}
                    className={`border-t border-slate-800 ${canDrill ? "cursor-pointer hover:bg-amber-500/5" : ""}`}
                    data-testid={`revenue-map-row-${r.key}`}
                  >
                    <td className="px-3 py-2 text-white flex items-center gap-2">
                      <Icon className="h-3 w-3 text-slate-500" /> {r.label}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-amber-400">${r.mrr_usd.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-emerald-400 font-semibold">{r.active_users ?? 0}</td>
                    <td className="px-3 py-2 text-right text-cyan-300">${(r.arpu_usd ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-300">{r.txn_count}</td>
                    <td className="px-3 py-2 text-right text-slate-500 font-mono text-xs">
                      {(r.latest_at || "").slice(0, 10)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HeatLegend() {
  return (
    <div className="bg-slate-900/80 rounded px-2 py-1 flex items-center gap-1">
      <span className="text-slate-500">Low</span>
      <div className="w-16 h-2 rounded" style={{
        background: `linear-gradient(to right, ${interpolateOranges(0.15)}, ${interpolateOranges(1)})`
      }}/>
      <span className="text-amber-400">High</span>
    </div>
  );
}

/**
 * Floating cursor-anchored callout rendered via portal into the viewport.
 * - Styled to match the dashboard dark theme (slate-950 glass + amber accent).
 * - Shifts away from the cursor edge so the pointer never covers it.
 * - Hidden when the hovered geography has zero MRR *and* zero users.
 */
function RevenueTooltip({ x, y, row }) {
  if (!row) return null;
  const empty = (row.mrr_usd || 0) === 0 && (row.active_users || 0) === 0 && !row._churn;
  // Position offset — 14px right / 14px below the cursor; flip to the left
  // when within 240px of the right edge to keep the card on-screen.
  const flipLeft = typeof window !== "undefined" && x + 240 > window.innerWidth;
  const flipUp = typeof window !== "undefined" && y + 160 > window.innerHeight;
  const style = {
    position: "fixed",
    left: flipLeft ? x - 240 : x + 14,
    top: flipUp ? y - 150 : y + 14,
    zIndex: 60,
    pointerEvents: "none",
  };
  const fmtUSD = (v) =>
    (v || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

  return (
    <div
      style={style}
      className="w-[220px] rounded-xl border border-amber-500/40 bg-slate-950/95 backdrop-blur-sm shadow-2xl shadow-black/60 overflow-hidden"
      data-testid="revenue-map-tooltip"
      role="tooltip"
    >
      <div className="px-3 py-2 bg-gradient-to-r from-amber-500/15 to-transparent border-b border-amber-500/30">
        <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold">Country</div>
        <div className="text-sm font-bold text-white truncate" data-testid="revenue-map-tooltip-label">
          {row.label}
        </div>
      </div>
      {empty ? (
        <div className="px-3 py-3 text-xs italic text-slate-500">
          {row._churn
            ? <>Top churn reason: <span className="text-rose-300 font-semibold not-italic">{row._churn.top_reason_label}</span> ({row._churn.churn_count})</>
            : "No paid transactions in this window yet."}
        </div>
      ) : (
        <dl className="px-3 py-2.5 space-y-1.5 text-xs">
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-400">Total Revenue</dt>
            <dd className="font-bold text-amber-400" data-testid="revenue-map-tooltip-mrr">
              {fmtUSD(row.mrr_usd)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-400">Active Users</dt>
            <dd className="font-semibold text-emerald-400" data-testid="revenue-map-tooltip-users">
              {row.active_users ?? 0}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-400">Avg Revenue / User</dt>
            <dd className="font-semibold text-cyan-300" data-testid="revenue-map-tooltip-arpu">
              {fmtUSD(row.arpu_usd)}
            </dd>
          </div>
          {row._churn && (
            <div className="mt-2 pt-2 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-wider text-rose-400 mb-0.5">Churn signal</div>
              <div className="flex items-baseline justify-between">
                <span className="text-slate-300 text-[11px]">Top reason</span>
                <span className="text-rose-300 font-semibold text-[11px]">
                  {row._churn.top_reason_label} · {row._churn.churn_count}
                </span>
              </div>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

// Complete ISO 3166-1 numeric → alpha-2 lookup.  Covers every geography
// in `countries-50m.json`, so every country rendered on the map is
// interactive (hover tooltip + drill-down) regardless of continent.
// Source: ISO 3166-1 canonical list, verified against world-atlas@2.
const ISO3_TO_ISO2 = {
  "004": "AF", "008": "AL", "010": "AQ", "012": "DZ", "016": "AS", "020": "AD",
  "024": "AO", "028": "AG", "031": "AZ", "032": "AR", "036": "AU", "040": "AT",
  "044": "BS", "048": "BH", "050": "BD", "051": "AM", "052": "BB", "056": "BE",
  "060": "BM", "064": "BT", "068": "BO", "070": "BA", "072": "BW", "074": "BV",
  "076": "BR", "084": "BZ", "086": "IO", "090": "SB", "092": "VG", "096": "BN",
  "100": "BG", "104": "MM", "108": "BI", "112": "BY", "116": "KH", "120": "CM",
  "124": "CA", "132": "CV", "136": "KY", "140": "CF", "144": "LK", "148": "TD",
  "152": "CL", "156": "CN", "158": "TW", "162": "CX", "166": "CC", "170": "CO",
  "174": "KM", "175": "YT", "178": "CG", "180": "CD", "184": "CK", "188": "CR",
  "191": "HR", "192": "CU", "196": "CY", "203": "CZ", "204": "BJ", "208": "DK",
  "212": "DM", "214": "DO", "218": "EC", "222": "SV", "226": "GQ", "231": "ET",
  "232": "ER", "233": "EE", "234": "FO", "238": "FK", "239": "GS", "242": "FJ",
  "246": "FI", "248": "AX", "250": "FR", "254": "GF", "258": "PF", "260": "TF",
  "262": "DJ", "266": "GA", "268": "GE", "270": "GM", "275": "PS", "276": "DE",
  "288": "GH", "292": "GI", "296": "KI", "300": "GR", "304": "GL", "308": "GD",
  "312": "GP", "316": "GU", "320": "GT", "324": "GN", "328": "GY", "332": "HT",
  "334": "HM", "336": "VA", "340": "HN", "344": "HK", "348": "HU", "352": "IS",
  "356": "IN", "360": "ID", "364": "IR", "368": "IQ", "372": "IE", "376": "IL",
  "380": "IT", "384": "CI", "388": "JM", "392": "JP", "398": "KZ", "400": "JO",
  "404": "KE", "408": "KP", "410": "KR", "414": "KW", "417": "KG", "418": "LA",
  "422": "LB", "426": "LS", "428": "LV", "430": "LR", "434": "LY", "438": "LI",
  "440": "LT", "442": "LU", "446": "MO", "450": "MG", "454": "MW", "458": "MY",
  "462": "MV", "466": "ML", "470": "MT", "474": "MQ", "478": "MR", "480": "MU",
  "484": "MX", "492": "MC", "496": "MN", "498": "MD", "499": "ME", "500": "MS",
  "504": "MA", "508": "MZ", "512": "OM", "516": "NA", "520": "NR", "524": "NP",
  "528": "NL", "531": "CW", "533": "AW", "534": "SX", "535": "BQ", "540": "NC",
  "548": "VU", "554": "NZ", "558": "NI", "562": "NE", "566": "NG", "570": "NU",
  "574": "NF", "578": "NO", "580": "MP", "581": "UM", "583": "FM", "584": "MH",
  "585": "PW", "586": "PK", "591": "PA", "598": "PG", "600": "PY", "604": "PE",
  "608": "PH", "612": "PN", "616": "PL", "620": "PT", "624": "GW", "626": "TL",
  "630": "PR", "634": "QA", "638": "RE", "642": "RO", "643": "RU", "646": "RW",
  "652": "BL", "654": "SH", "659": "KN", "660": "AI", "662": "LC", "663": "MF",
  "666": "PM", "670": "VC", "674": "SM", "678": "ST", "682": "SA", "686": "SN",
  "688": "RS", "690": "SC", "694": "SL", "702": "SG", "703": "SK", "704": "VN",
  "705": "SI", "706": "SO", "710": "ZA", "716": "ZW", "724": "ES", "728": "SS",
  "729": "SD", "732": "EH", "740": "SR", "744": "SJ", "748": "SZ", "752": "SE",
  "756": "CH", "760": "SY", "762": "TJ", "764": "TH", "768": "TG", "772": "TK",
  "776": "TO", "780": "TT", "784": "AE", "788": "TN", "792": "TR", "795": "TM",
  "796": "TC", "798": "TV", "800": "UG", "804": "UA", "807": "MK", "818": "EG",
  "826": "GB", "831": "GG", "832": "JE", "833": "IM", "834": "TZ", "840": "US",
  "850": "VI", "854": "BF", "858": "UY", "860": "UZ", "862": "VE", "876": "WF",
  "882": "WS", "887": "YE", "894": "ZM",
};
